import React, { useState, useMemo, useRef, useEffect } from 'react';

function calcularAvancesBottomUp(actividades) {
  const childrenMap = {};
  for (const act of actividades) {
    if (!act.wbsCode) continue;
    const parts = act.wbsCode.split('.');
    if (parts.length > 1) {
      const parentCode = parts.slice(0, -1).join('.');
      if (!childrenMap[parentCode]) childrenMap[parentCode] = [];
      childrenMap[parentCode].push(act);
    }
  }
  const avanceCalc = {};
  const durCalc = {};
  const sorted = [...actividades].sort(
    (a, b) => (b.wbsCode?.split('.').length || 0) - (a.wbsCode?.split('.').length || 0)
  );
  for (const act of sorted) {
    if (!act.wbsCode) continue;
    const children = childrenMap[act.wbsCode] || [];
    if (children.length === 0) {
      avanceCalc[act.wbsCode] = act.porcentajeAvance || 0;
      durCalc[act.wbsCode] = act.duracionBase || 1;
    } else {
      let sumDur = 0, sumAvance = 0;
      for (const child of children) {
        const dur = durCalc[child.wbsCode] ?? child.duracionBase ?? 1;
        sumDur += dur;
        sumAvance += (avanceCalc[child.wbsCode] ?? 0) * dur;
      }
      avanceCalc[act.wbsCode] = sumDur > 0 ? Math.round(sumAvance / sumDur) : 0;
      durCalc[act.wbsCode] = sumDur;
    }
  }
  return avanceCalc;
}

function construirRuta(wbs, actMap) {
  const partes = wbs.split('.');
  const labels = [];
  for (let i = 1; i <= partes.length; i++) {
    const code = partes.slice(0, i).join('.');
    const act = actMap[code];
    if (act) labels.push(act.nombre);
  }
  return labels.join(' › ');
}

function pctColor(pct) {
  if (pct === 100) return { bar: '#16a34a', text: '#16a34a', bg: '#f0fdf4' };
  if (pct >= 70)   return { bar: '#2563eb', text: '#2563eb', bg: '#eff6ff' };
  if (pct >= 30)   return { bar: '#ea580c', text: '#ea580c', bg: '#fff7ed' };
  return             { bar: '#dc2626', text: '#dc2626', bg: '#fef2f2' };
}

function DropdownPct({ label, value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find(o => o.value === value);
  const selPct = selected?.pct ?? null;
  const selColor = selPct !== null ? pctColor(selPct) : null;

  return (
    <div className="programa-version-selector">
      <label>{label}</label>
      <div className="prog-dropdown" ref={ref}>
        <button
          type="button"
          className={`prog-dropdown-trigger${open ? ' open' : ''}`}
          onClick={() => setOpen(v => !v)}
        >
          <span className="trigger-label">
            {selected ? selected.label : placeholder}
          </span>
          {selPct !== null && (
            <span
              className="trigger-badge"
              style={{ background: selColor.bg, color: selColor.text }}
            >
              {selPct}%
            </span>
          )}
          <span className="trigger-arrow">▼</span>
        </button>

        {open && (
          <div className="prog-dropdown-panel">
            <div
              className={`prog-dropdown-item reset-item${!value ? ' selected' : ''}`}
              onClick={() => { onChange(''); setOpen(false); }}
            >
              {placeholder}
            </div>
            {options.map(opt => {
              const c = pctColor(opt.pct);
              return (
                <div
                  key={opt.value}
                  className={`prog-dropdown-item${value === opt.value ? ' selected' : ''}`}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                >
                  <span className="item-name" title={opt.label}>{opt.label}</span>
                  <div className="item-right">
                    <span className="item-pct" style={{ color: c.text }}>{opt.pct}%</span>
                    <div className="item-bar-wrap">
                      <div
                        className="item-bar-fill"
                        style={{ width: `${opt.pct}%`, background: c.bar }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function celda(pct) {
  if (pct === null) return { bg: '#f8fafc', color: '#cbd5e1', text: '—', fw: 400 };
  if (pct === 100)  return { bg: '#f0fdf4', color: '#16a34a', text: '100%', fw: 700 };
  if (pct >= 50)    return { bg: '#eff6ff', color: '#2563eb', text: `${pct}%`, fw: 600 };
  if (pct >= 1)     return { bg: '#fff7ed', color: '#ea580c', text: `${pct}%`, fw: 600 };
  return             { bg: '#fef2f2', color: '#dc2626', text: '0%', fw: 400 };
}

function headerColor(pct) {
  if (pct === 100) return '#16a34a';
  if (pct >= 70)   return '#2563eb';
  if (pct >= 30)   return '#ea580c';
  return '#dc2626';
}

export default function TablaAvancePivot({ actividades, secciones, avancePorSeccionTabla, mostrarTodos = false }) {
  const [seccionId, setSeccionId] = useState('');
  const [grupoWbs, setGrupoWbs] = useState('');

  const avanceMap = useMemo(() => calcularAvancesBottomUp(actividades), [actividades]);

  // % real por sección: agrupa hojas por su campo `seccion` (_id de SeccionPrograma)
  // avanceMap[s.wbsCode] no funciona porque los nodos-sección no son actividades
  const seccionPctMap = useMemo(() => {
    const allWbs = new Set(actividades.map(a => a.wbsCode).filter(Boolean));
    const parents = new Set();
    for (const code of allWbs) {
      const parts = code.split('.');
      for (let i = 1; i < parts.length; i++) parents.add(parts.slice(0, i).join('.'));
    }
    const grupos = {};
    for (const act of actividades) {
      if (!act.wbsCode || parents.has(act.wbsCode)) continue; // solo hojas
      const secId = act.seccion?._id ? String(act.seccion._id) : String(act.seccion || '');
      if (!secId) continue;
      if (!grupos[secId]) grupos[secId] = { sumDur: 0, sumAv: 0 };
      const dur = act.duracionBase || 1;
      grupos[secId].sumDur += dur;
      grupos[secId].sumAv += (act.porcentajeAvance || 0) * dur;
    }
    const result = {};
    for (const [id, { sumDur, sumAv }] of Object.entries(grupos)) {
      result[id] = sumDur > 0 ? Math.round(sumAv / sumDur) : 0;
    }
    return result;
  }, [actividades]);

  const actividadesSeccion = useMemo(() => {
    if (!seccionId) return [];
    return actividades.filter(a =>
      a.seccion === seccionId || a.seccion?._id === seccionId || String(a.seccion) === seccionId
    );
  }, [actividades, seccionId]);

  const gruposPivote = useMemo(() => {
    if (!actividadesSeccion.length) return [];

    const wbsSet = new Set(actividadesSeccion.map(a => a.wbsCode).filter(Boolean));
    const actMap = Object.fromEntries(actividadesSeccion.map(a => [a.wbsCode, a]));

    // Hojas = sin hijos, O(n): pre-construir conjunto de padres
    const parents = new Set();
    for (const code of wbsSet) {
      const parts = code.split('.');
      for (let i = 1; i < parts.length; i++) {
        parents.add(parts.slice(0, i).join('.'));
      }
    }
    const hojas = actividadesSeccion.filter(a => a.wbsCode && !parents.has(a.wbsCode));

    // hojas → padre
    const hojasPorPadre = {};
    for (const h of hojas) {
      const parentWbs = h.wbsCode.split('.').slice(0, -1).join('.');
      if (!hojasPorPadre[parentWbs]) hojasPorPadre[parentWbs] = [];
      hojasPorPadre[parentWbs].push(h);
    }

    // padres → abuelo
    const padresPorAbuelo = {};
    for (const parentWbs of Object.keys(hojasPorPadre)) {
      const grandWbs = parentWbs.split('.').slice(0, -1).join('.');
      if (!padresPorAbuelo[grandWbs]) padresPorAbuelo[grandWbs] = [];
      padresPorAbuelo[grandWbs].push(parentWbs);
    }

    const grupos = [];
    for (const [grandWbs, parentWbsList] of Object.entries(padresPorAbuelo)) {
      if (parentWbsList.length < 2) continue;

      const grandAct = actMap[grandWbs];
      const columnas = parentWbsList
        .map(pw => actMap[pw])
        .filter(Boolean)
        .sort((a, b) => a.orden - b.orden);

      const rutaHeader = construirRuta(grandWbs, actMap);

      // Filas: unión ordenada de nombres de hojas
      const nombresSeen = new Set();
      const filas = [];
      for (const col of columnas) {
        for (const hoja of (hojasPorPadre[col.wbsCode] || [])) {
          if (!nombresSeen.has(hoja.nombre)) {
            nombresSeen.add(hoja.nombre);
            filas.push(hoja.nombre);
          }
        }
      }

      // Datos del pivote
      const pivotData = {};
      for (const nombre of filas) {
        pivotData[nombre] = {};
        for (const col of columnas) {
          const match = (hojasPorPadre[col.wbsCode] || []).find(h => h.nombre === nombre);
          pivotData[nombre][col.wbsCode] = match != null
            ? (avanceMap[match.wbsCode] ?? match.porcentajeAvance ?? 0)
            : null;
        }
      }

      grupos.push({
        grandWbs,
        orden: grandAct?.orden ?? 9999,
        rutaHeader,
        columnas,
        filas,
        pivotData,
      });
    }

    return grupos.sort((a, b) => a.orden - b.orden);
  }, [actividadesSeccion, avanceMap]);

  const seccionesOrdenadas = [...secciones].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));

  // Solo grupos que tienen al menos una fila pendiente para mostrar en el dropdown
  const gruposConPendientes = gruposPivote.filter(g => {
    const colsInc = g.columnas.filter(col => (avanceMap[col.wbsCode] ?? 0) < 100);
    if (!colsInc.length) return false;
    return g.filas.some(nombre => {
      const vals = colsInc.map(col => g.pivotData[nombre]?.[col.wbsCode]);
      return vals.some(v => v !== null) && vals.some(v => v !== null && v < 100);
    });
  });

  const gruposParaDropdown = mostrarTodos ? gruposPivote : gruposConPendientes;

  const opcionesSecciones = seccionesOrdenadas.map(s => ({
    value: s._id,
    label: s.nombre,
    pct: seccionPctMap[String(s._id)] ?? 0,
  }));

  const opcionesGrupos = gruposParaDropdown.map(g => ({
    value: g.grandWbs,
    label: g.rutaHeader,
    pct: avanceMap[g.grandWbs] ?? 0,
  }));

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <DropdownPct
          label="Sección"
          value={seccionId}
          onChange={v => { setSeccionId(v); setGrupoWbs(''); }}
          options={opcionesSecciones}
          placeholder="— Ver resumen general —"
        />

        {seccionId && gruposParaDropdown.length > 0 && (
          <DropdownPct
            label={`Grupo (${gruposParaDropdown.length} ${mostrarTodos ? 'grupos' : 'pendientes'})`}
            value={grupoWbs}
            onChange={setGrupoWbs}
            options={opcionesGrupos}
            placeholder="— Ver todos —"
          />
        )}

        {seccionId && (
          <button
            className="programa-btn programa-btn-secondary"
            style={{ fontSize: '0.8rem', padding: '5px 12px' }}
            onClick={() => { setSeccionId(''); setGrupoWbs(''); }}
          >
            ← Resumen
          </button>
        )}
      </div>

      {/* Sin sección → resumen por sección */}
      {!seccionId && avancePorSeccionTabla}

      {/* Con sección seleccionada → sub-tablas auto-generadas */}
      {seccionId && gruposPivote.length === 0 && (
        <div style={{ color: '#94a3b8', fontSize: '0.9rem', padding: '24px 0', textAlign: 'center' }}>
          Esta sección no tiene grupos con estructura repetida (torres/fases) para mostrar como tabla.
        </div>
      )}

      {seccionId && gruposPivote
        .filter(g => !grupoWbs || g.grandWbs === grupoWbs)
        .map((grupo) => {
          if (mostrarTodos) return grupo;
          // Filtrar columnas incompletas y filas incompletas
          const columnasIncompletas = grupo.columnas.filter(
            col => (avanceMap[col.wbsCode] ?? 0) < 100
          );
          const filasIncompletas = grupo.filas.filter(nombre => {
            const vals = columnasIncompletas.map(col => grupo.pivotData[nombre]?.[col.wbsCode]);
            const existeEnAlguna = vals.some(v => v !== null);
            const tieneIncompleto = vals.some(v => v !== null && v < 100);
            return existeEnAlguna && tieneIncompleto;
          });
          if (columnasIncompletas.length === 0 || filasIncompletas.length === 0) return null;
          return { ...grupo, columnas: columnasIncompletas, filas: filasIncompletas };
        })
        .filter(Boolean)
        .map((grupo, gi) => {
        const rutaParts = grupo.rutaHeader.split(' › ');
        const titulo = rutaParts.slice(-1)[0];
        const prefijo = rutaParts.slice(0, -1).join(' › ');

        return (
          <div key={gi} style={{ marginBottom: 36 }}>
            {/* Header jerárquico */}
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: '8px',
              paddingBottom: '8px', borderBottom: '2px solid #111827', marginBottom: '12px'
            }}>
              {prefijo && (
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{prefijo} ›</span>
              )}
              <span style={{ fontSize: '0.97rem', fontWeight: 700, color: '#1e293b' }}>{titulo}</span>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginLeft: 6 }}>
                {grupo.columnas.length} grupos · {grupo.filas.length} partida{grupo.filas.length !== 1 ? 's' : ''}{!mostrarTodos ? ' sin completar' : ''}
              </span>
            </div>

            {/* Tabla pivote */}
            <div className="programa-tabla-wrapper" style={{ overflowX: 'auto' }}>
              <table className="programa-tabla" style={{ minWidth: `${280 + grupo.columnas.length * 115}px` }}>
                <thead>
                  <tr>
                    <th style={{
                      textAlign: 'left', paddingLeft: 12,
                      fontSize: '0.72rem', color: '#64748b', fontWeight: 600,
                      background: '#f8fafc', minWidth: 260
                    }}>
                      PARTIDA
                    </th>
                    {grupo.columnas.map(col => {
                      const pct = avanceMap[col.wbsCode] ?? 0;
                      const color = headerColor(pct);
                      return (
                        <th key={col.wbsCode} style={{
                          textAlign: 'center', minWidth: 110,
                          padding: '8px 6px', background: '#f8fafc'
                        }}>
                          <div style={{
                            fontSize: '0.73rem', fontWeight: 700, color: '#1e293b',
                            marginBottom: 3, lineHeight: 1.2
                          }}>
                            {col.nombre}
                          </div>
                          <div style={{ fontSize: '0.92rem', fontWeight: 800, color }}>
                            {pct}%
                          </div>
                          <div style={{
                            height: 3, background: '#e2e8f0', borderRadius: 2,
                            margin: '4px 10px 0', overflow: 'hidden'
                          }}>
                            <div style={{
                              height: '100%', borderRadius: 2, width: `${pct}%`,
                              background: color, transition: 'width 0.5s ease'
                            }} />
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {grupo.filas.map((nombre, fi) => (
                    <tr key={fi} className="programa-fila-actividad">
                      <td style={{
                        paddingLeft: 12, paddingRight: 8,
                        fontSize: '0.8rem', color: '#334155',
                        maxWidth: 260, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }} title={nombre}>
                        {nombre}
                      </td>
                      {grupo.columnas.map(col => {
                        const pct = grupo.pivotData[nombre]?.[col.wbsCode];
                        const s = celda(pct);
                        return (
                          <td key={col.wbsCode} style={{
                            textAlign: 'center', padding: '5px 4px',
                            background: s.bg
                          }}>
                            <span style={{
                              fontSize: '0.8rem', fontWeight: s.fw, color: s.color
                            }}>
                              {s.text}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Leyenda */}
      {seccionId && gruposPivote.filter(g => !grupoWbs || g.grandWbs === grupoWbs).length > 0 && (
        <div style={{ display: 'flex', gap: '14px', marginTop: '8px', flexWrap: 'wrap' }}>
          {[
            { bg: '#fef2f2', color: '#dc2626', label: '0%' },
            { bg: '#fff7ed', color: '#ea580c', label: '1–49%' },
            { bg: '#eff6ff', color: '#2563eb', label: '50–99%' },
            { bg: '#f0fdf4', color: '#16a34a', label: '100%' },
            { bg: '#f8fafc', color: '#cbd5e1', label: 'No aplica' },
          ].map(({ bg, color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem' }}>
              <div style={{ width: 12, height: 12, background: bg, border: `1.5px solid ${color}`, borderRadius: 2 }} />
              <span style={{ color: '#64748b' }}>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
